(function () {
  'use strict';

  var regions = ['ENG', 'SCT', 'WLS', 'NIR', 'BFP'];
  var selections = new Map();
  var recommendationPromise;

  function regionCode(value) {
    var code = String(value || '').toUpperCase().replace(/^GB-/, '');
    return regions.indexOf(code) === -1 ? '' : code;
  }

  function allowedCountry(config, country) {
    var code = String(country || '').toUpperCase();
    return (config.countries || []).some(function (entry) { return entry.code === code; }) ? code : '';
  }

  function normalizedSelection(config, value) {
    if (!value || typeof value !== 'object') return null;
    var country = allowedCountry(config, value.country);
    return country ? { country: country, region: country === 'GB' ? regionCode(value.region) : '' } : null;
  }

  function recommendationCountry(config, payload) {
    var detected = payload && payload.detected_values && payload.detected_values.country;
    var code = allowedCountry(config, detected && detected.handle);
    if (code) return code;
    var suggestions = payload && Array.isArray(payload.suggestions) ? payload.suggestions : [];
    suggestions = suggestions.slice().sort(function (a, b) { return Number(Boolean(b.is_preferred)) - Number(Boolean(a.is_preferred)); });
    for (var i = 0; i < suggestions.length; i += 1) {
      var country = suggestions[i].parts && suggestions[i].parts.country;
      code = allowedCountry(config, country && country.handle);
      if (code) return code;
    }
    return '';
  }

  function shippingRule(config, selection) {
    if (!selection || !selection.country || !config.rules) return null;
    var group = config.isB2b ? config.rules.b2b : config.rules.b2c;
    if (!group) return null;
    var country = selection.country;
    if (config.isB2b && country !== 'DE' && (config.rules.euCountries || []).indexOf(country) === -1) return { review: true };
    if (country === 'GB') {
      if (!regionCode(selection.region)) return { regionRequired: true };
      return selection.region === 'NIR' || selection.region === 'BFP' ? group.eu : group.gb;
    }
    if (country === 'DE') return group.de;
    if (country === 'CH') return group.ch;
    if ((config.rules.euCountries || []).indexOf(country) !== -1) return group.eu;
    return group.world;
  }

  function noticeState(config, basis, selection) {
    if (!basis || !selection) return { kind: 'fallback' };
    if (Number(basis.itemCount) === 0) return { kind: 'empty' };
    if (basis.requiresShipping === false) return { kind: 'digital' };
    var rule = shippingRule(config, selection);
    if (!rule) return { kind: 'fallback' };
    if (rule.regionRequired) return { kind: 'region' };
    if (rule.review) return { kind: 'review' };
    var precise = basis.valid === true && !basis.hasUnverifiedDigital &&
      basis.currency === 'EUR' && config.currency === 'EUR' &&
      selection.country === basis.contextCountry && basis.isB2b === config.isB2b &&
      Number.isSafeInteger(basis.amount) && basis.amount >= 0;
    var state = {
      kind: 'fee',
      fee: rule.fee,
      threshold: rule.threshold,
      minimum: rule.minimum,
      precise: precise,
      contextMismatch: selection.country !== basis.contextCountry,
      currencyMismatch: basis.currency !== 'EUR' || config.currency !== 'EUR',
      unverifiedDigital: Boolean(basis.hasUnverifiedDigital)
    };
    if (Number.isSafeInteger(rule.threshold) && rule.threshold > 0) {
      state.kind = 'threshold';
      if (precise) {
        state.remaining = Math.max(0, rule.threshold - basis.amount);
        state.progress = Math.min(100, Math.max(0, basis.amount / rule.threshold * 100));
        state.kind = state.remaining === 0 ? 'qualified' : 'remaining';
      }
    } else if (Number.isSafeInteger(rule.minimum) && rule.minimum > 0) {
      state.kind = 'minimum';
    }
    return state;
  }

  function interpolate(template, values) {
    return String(template || '').replace(/__(amount|country|threshold|fee|minimum)__/g, function (token, key) {
      return values[key] === undefined ? token : values[key];
    });
  }

  function money(config, cents) {
    try {
      return new Intl.NumberFormat(config.language || 'de', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2) + ' EUR';
    }
  }

  var helpers = {
    regionCode: regionCode,
    normalizedSelection: normalizedSelection,
    recommendationCountry: recommendationCountry,
    shippingRule: shippingRule,
    noticeState: noticeState,
    interpolate: interpolate,
    money: money
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (typeof window === 'undefined' || typeof document === 'undefined' || !window.customElements) return;
  if (window.customElements.get('hokema-cart-shipping')) return;

  function recommendations(config) {
    if (!recommendationPromise) {
      recommendationPromise = (async function () {
        var controller = typeof AbortController === 'undefined' ? null : new AbortController();
        var timer = controller ? window.setTimeout(function () { controller.abort(); }, 6000) : null;
        try {
          var root = new URL(config.rootUrl || '/', window.location.origin);
          if (root.origin !== window.location.origin) return null;
          if (!root.pathname.endsWith('/')) root.pathname += '/';
          var url = new URL('browsing_context_suggestions.json', root);
          url.searchParams.set('country[enabled]', 'true');
          url.searchParams.set('language[enabled]', 'false');
          var response = await window.fetch(url.toString(), {
            method: 'GET',
            credentials: 'omit',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: controller ? controller.signal : undefined
          });
          return response.ok ? await response.json() : null;
        } catch (error) {
          return null;
        } finally {
          if (timer) window.clearTimeout(timer);
        }
      })();
    }
    return recommendationPromise;
  }

  class HokemaCartShipping extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      var script = this.querySelector('[data-hk-shipping-config]');
      try { this.config = JSON.parse(script ? script.textContent : '{}'); } catch (error) { return; }
      this.countryInput = this.querySelector('[data-hk-shipping-country]');
      this.regionInput = this.querySelector('[data-hk-shipping-region]');
      this.regionHolder = this.querySelector('[data-hk-shipping-region-holder]');
      this.destination = this.querySelector('[data-hk-shipping-destination]');
      this.status = this.querySelector('[data-hk-shipping-status]');
      this.progress = this.querySelector('[data-hk-shipping-progress]');
      this.note = this.querySelector('[data-hk-shipping-note]');
      this.scope = this.closest('cart-items') || this.parentElement;
      if (!this.countryInput || !this.regionInput || !this.status || !this.scope) return;
      this.initialized = true;
      this.generation = (this.generation || 0) + 1;
      var generation = this.generation;
      this.key = this.config.storageKey || (this.config.isB2b ? 'hokema-shipping-b2b' : 'hokema-shipping-b2c');
      this.manual = this.readSelection();
      this.selection = this.manual || normalizedSelection(this.config, {
        country: this.config.isB2b && this.config.companyCountry ? this.config.companyCountry : this.config.contextCountry,
        region: this.config.companyRegion
      });
      this.onChange = this.handleChange.bind(this);
      this.onRefresh = this.refresh.bind(this);
      this.onSelection = this.handleSelection.bind(this);
      this.countryInput.addEventListener('change', this.onChange);
      this.regionInput.addEventListener('change', this.onChange);
      ['theme:cart:added', 'theme:cart:loaded', 'theme:cart:open', 'shopify:section:load'].forEach((name) => {
        document.addEventListener(name, this.onRefresh);
      });
      document.addEventListener('hokema:shipping:destination', this.onSelection);
      window.addEventListener('storage', this.onSelection);
      var controls = this.querySelector('[data-hk-shipping-controls]');
      if (controls) controls.hidden = false;
      this.refresh();
      this.observer = new MutationObserver(() => {
        var current = this.basisScript();
        if ((current ? current.textContent : '') !== this.basisSignature) this.refresh();
      });
      this.observer.observe(this.scope, { childList: true, subtree: true });
      if (!this.manual && !(this.config.isB2b && this.config.companyCountry)) {
        recommendations(this.config).then((payload) => {
          if (!this.initialized || generation !== this.generation || this.manual) return;
          var country = recommendationCountry(this.config, payload);
          if (country) this.selection = { country: country, region: '' };
          this.refresh();
        });
      }
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      this.generation += 1;
      this.countryInput.removeEventListener('change', this.onChange);
      this.regionInput.removeEventListener('change', this.onChange);
      ['theme:cart:added', 'theme:cart:loaded', 'theme:cart:open', 'shopify:section:load'].forEach((name) => {
        document.removeEventListener(name, this.onRefresh);
      });
      document.removeEventListener('hokema:shipping:destination', this.onSelection);
      window.removeEventListener('storage', this.onSelection);
      if (this.observer) this.observer.disconnect();
    }

    readSelection() {
      var value = selections.get(this.key);
      if (!value) {
        try { value = JSON.parse(window.localStorage.getItem(this.key)); } catch (error) { value = null; }
      }
      return normalizedSelection(this.config, value);
    }

    handleChange(event) {
      var country = this.countryInput.value;
      var region = country === 'GB' ? this.regionInput.value : '';
      if (event.target === this.countryInput && (!this.selection || this.selection.country !== country)) region = '';
      var value = normalizedSelection(this.config, { country: country, region: region });
      if (!value) return;
      this.manual = value;
      this.selection = value;
      selections.set(this.key, value);
      try { window.localStorage.setItem(this.key, JSON.stringify(value)); } catch (error) {}
      document.dispatchEvent(new CustomEvent('hokema:shipping:destination', { detail: { key: this.key, selection: value } }));
      this.refresh();
    }

    handleSelection(event) {
      if (event.type === 'storage') {
        if (event.key !== this.key) return;
        var stored;
        try { stored = JSON.parse(event.newValue); } catch (error) { stored = null; }
        var value = normalizedSelection(this.config, stored);
        if (!value) return;
        selections.set(this.key, value);
      } else {
        if (!event.detail || event.detail.key !== this.key) return;
        var selected = normalizedSelection(this.config, event.detail.selection);
        if (!selected) return;
        selections.set(this.key, selected);
      }
      this.manual = this.readSelection();
      if (this.manual) this.selection = this.manual;
      this.refresh();
    }

    basisScript() {
      return this.scope.querySelector('[data-hk-shipping-basis]');
    }

    refresh() {
      if (!this.initialized) return;
      var script = this.basisScript();
      this.basisSignature = script ? script.textContent : '';
      var basis;
      try { basis = JSON.parse(this.basisSignature); } catch (error) { basis = null; }
      var state = noticeState(this.config, basis, this.selection);
      this.setAttribute('data-shipping-state', state.kind);
      this.hidden = state.kind === 'empty' || state.kind === 'digital';
      this.countryInput.value = this.selection ? this.selection.country : '';
      this.regionInput.value = this.selection ? this.selection.region : '';
      if (this.regionHolder) this.regionHolder.hidden = this.config.isB2b || !this.selection || this.selection.country !== 'GB';
      var labels = this.config.labels || {};
      var country = this.selection && (this.config.countries || []).find((entry) => entry.code === this.selection.country);
      var regionLabels = { ENG: 'england', SCT: 'scotland', WLS: 'wales', NIR: 'northern_ireland', BFP: 'british_forces' };
      var destination = country ? country.name : '';
      if (this.selection && this.selection.country === 'GB' && this.selection.region) {
        destination += ' · ' + (labels[regionLabels[this.selection.region]] || this.selection.region);
      }
      if (this.destination) this.destination.textContent = destination;
      var values = {
        country: destination,
        amount: money(this.config, state.remaining || 0),
        threshold: money(this.config, state.threshold || 0),
        fee: money(this.config, state.fee || 0),
        minimum: money(this.config, state.minimum || 0)
      };
      var labelKeys = { empty: 'fallback', fallback: 'fallback', digital: 'digital_note', region: 'region_required', review: 'review_shipping', fee: 'flat_fee', threshold: 'threshold', minimum: 'gb_minimum', remaining: 'remaining', qualified: 'qualified' };
      this.status.textContent = interpolate(labels[labelKeys[state.kind]], values);
      if (this.progress) {
        var showProgress = state.kind === 'remaining' && Number.isFinite(state.progress);
        this.progress.hidden = !showProgress;
        this.progress.value = showProgress ? state.progress : 0;
        if (showProgress) {
          this.progress.max = 100;
          this.progress.setAttribute('aria-label', this.status.textContent);
        }
      }
      var notes = [];
      if (state.kind !== 'digital' && state.kind !== 'empty') {
        if (state.contextMismatch && labels.context_note) notes.push(labels.context_note);
        if (state.currencyMismatch && labels.currency_note) notes.push(labels.currency_note);
        if (state.unverifiedDigital && labels.digital_note) notes.push(labels.digital_note);
        if (labels.estimate_note) notes.push(labels.estimate_note);
        if (this.config.isB2b && labels.net_note) notes.push(labels.net_note);
      }
      if (this.note) {
        this.note.textContent = notes.map((text) => interpolate(text, values)).join(' ');
        this.note.hidden = notes.length === 0;
      }
    }
  }

  window.customElements.define('hokema-cart-shipping', HokemaCartShipping);
})();
